import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit } from '@angular/core'
import { LucideIconData } from '@lucide/angular'
import { Observable, Subject, of } from 'rxjs'
import { catchError, map, takeUntil, timeout } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { ApplicationProviderService } from 'src/app/account/services/application.service'
import { AccountService } from 'src/app/account/services/account.service'
import { UsageBillingService } from 'src/app/account/components/usage-billing/usage-billing.service'
import { StacksetsRegenerationPollService } from 'src/app/shared/services/stacksets-regeneration-poll.service'
import { SHOWCASE_SECTIONS, ShowcaseSection } from './showcase-registry'
import { TestPageMockState } from './showcase-mock-state'
import {
  ShowcasePatchDeps,
  ShowcasePatchedOriginals,
  installShowcasePatches,
  restoreShowcasePatches,
} from './showcase-mock-patches'
import { buildToastGroups } from './mocks/showcase-toast-mocks'

/**
 * Shell for the internal showcase at /test_page.
 *
 * It owns the access gate, the page chrome (nav pills, accordions) and the
 * singleton patches; each section under sections/ owns its own markup, styles,
 * state and mocks and is mounted lazily on expand. Adding a showcase should not
 * touch this file — see showcase-registry.ts.
 */

/**
 * Lucide's `toast` lives in @lucide/lab, which the app does not depend on, so the icon travels
 * as data instead. `LucideDynamicIcon` takes it the same way the error glyphs are rendered.
 * Source: @lucide/lab v0.7.0 (ISC), https://lucide.dev/icons/lab/toast
 */
const TOAST_ICON: LucideIconData = {
  name: 'toast',
  node: [
    ['path', { d: 'M5.5 3A3.5 3.5 0 0 0 3 8.9V19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.9A3.5 3.5 0 0 0 18.5 3Z' }],
    [
      'path',
      {
        d: 'M7.5 10c0-1.8 1.2-3 3.2-3 2.5 0 2.4 1.5 3.8 2.5s2.5 1 2.5 3c0 2.2-1.2 3.2-3.5 3.2-1.2 0-1.2 1.2-3 1.2S7 16 7 14.2c0-1.5.8-1.5.8-2.5 0-.7-.3-1.2-.3-1.7',
      },
    ],
  ],
}

@Component({
  selector: 'app-test-page',
  templateUrl: './test-page.component.html',
  styleUrls: ['./test-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  providers: [TestPageMockState],
})
export class TestPageComponent implements OnInit, OnDestroy {
  readonly toastIcon = TOAST_ICON

  // 'loading' → spinner; 'allowed' → showcase; 'denied' → standard 404.
  // Gated on `principal.adminRole != null` — any non-null AdminRoleDto
  // (readOnly / accountManager / full) qualifies as a "legacy admin",
  // matching how access-level.service derives `isLegacyAdmin`.
  gateState: 'loading' | 'allowed' | 'denied' = 'loading'

  readonly sections = SHOWCASE_SECTIONS
  readonly expandedSections: { [key: string]: boolean } = Object.fromEntries(
    SHOWCASE_SECTIONS.map((s) => [s.key, false])
  )

  readonly toastGroups = buildToastGroups(this.uiService)
  toastMenuOpen = false

  private patchedOriginals: ShowcasePatchedOriginals = {}
  // Cancels in-flight gate / data subscriptions on destroy — without this a late
  // HTTP response could patch singletons on a destroyed instance with no future
  // ngOnDestroy to restore them.
  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly uiService: UiService,
    private readonly apiService: ApiService,
    private readonly applicationProviderService: ApplicationProviderService,
    private readonly accountService: AccountService,
    private readonly accessLevelService: AccessLevelService,
    private readonly regenerationPollService: StacksetsRegenerationPollService,
    private readonly usageBillingService: UsageBillingService,
    private readonly state: TestPageMockState
  ) {}

  private get patchDeps(): ShowcasePatchDeps {
    return {
      apiService: this.apiService,
      accountService: this.accountService,
      accessLevelService: this.accessLevelService,
      applicationProviderService: this.applicationProviderService,
      usageBillingService: this.usageBillingService,
      regenerationPollService: this.regenerationPollService,
    }
  }

  ngOnInit(): void {
    // Mock setup only runs in the allowed branch — customers who somehow hit
    // /test_page never get singletons monkey-patched.
    this.gate$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((allowed) => {
        if (!allowed) {
          this.gateState = 'denied'
          return
        }
        this.gateState = 'allowed'
        this.state.seedMocks()
        this.patchedOriginals = installShowcasePatches(this.patchDeps, this.state)
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    // Restore patched singletons so navigating away returns the SPA to real
    // entitlement / permission / API behavior.
    restoreShowcasePatches(this.patchDeps, this.state, this.patchedOriginals)
    // Flush seeded mocks; the next account view refetches from the API.
    this.state.seedAppCache([])
  }

  toggleSection(key: string): void {
    this.expandedSections[key] = !this.expandedSections[key]
  }

  scrollTo(section: ShowcaseSection): void {
    // Expand first, then scroll once Angular has rendered the section body.
    this.expandedSections[section.key] = true
    setTimeout(() => {
      document.getElementById(section.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  /**
   * Allow only legacy admins. `principal.adminRole != null` is what
   * access-level.service uses to derive `isLegacyAdmin`. Any failure or 10s hang
   * resolves to denied (404). This is the showcase's only network call.
   */
  private gate$(): Observable<boolean> {
    return this.apiService.getAuthenticatedPrincipal().pipe(
      map((principal) => principal.adminRole != null),
      timeout(10000),
      catchError((err) => {
        console.warn('Test page: gate failed —', err)
        return of(false)
      })
    )
  }

  // -------- Toast tester --------

  toggleToastMenu(event: MouseEvent): void {
    event.stopPropagation()
    event.preventDefault()
    this.toastMenuOpen = !this.toastMenuOpen
  }

  fireToast(event: MouseEvent, fire: () => void): void {
    event.stopPropagation()
    event.preventDefault()
    fire()
    this.toastMenuOpen = false
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation()
  }

  @HostListener('document:click')
  closeToastMenuOnDocClick(): void {
    if (this.toastMenuOpen) this.toastMenuOpen = false
  }

  @HostListener('document:keydown.escape')
  closeToastMenuOnEscape(): void {
    if (this.toastMenuOpen) this.toastMenuOpen = false
  }
}
